import React, { useState } from 'react';
import './App.css';
import menuData from './data/menu.json';

interface MenuItem {
  date: string;
  day: string;
  starter: string;
  mainCourse: string;
  side: string;
  dairy: string;
  dessert: string;
  vege?: boolean;
}

const App: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const getMenuForDate = (date: Date): MenuItem | null => {
    const dateString = date.toISOString().split('T')[0];
    return (menuData as Record<string, MenuItem>)[dateString] || null;
  };

  const isWeekend = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Dimanche, 6 = Samedi
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const navigateDate = (days: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const selectedMenu = getMenuForDate(selectedDate);
  const isToday = selectedDate.toDateString() === new Date().toDateString();

  if (isWeekend(selectedDate)) {
    return (
      <div className="app">
        <div className="container">
          <header className="header">
            <h1>🍽️ Cantine Scolaire</h1>
            <p className="subtitle">Mairie de Parmain</p>
            
            <div className="date-navigation">
              <div className="nav-buttons">
                <button onClick={() => navigateDate(-1)} className="nav-btn">
                  ← Jour précédent
                </button>
                {!isToday && (
                  <button onClick={goToToday} className="today-btn">
                    Aujourd'hui
                  </button>
                )}
                <button onClick={() => navigateDate(1)} className="nav-btn">
                  Jour suivant →
                </button>
              </div>
              
              <div className="date-picker-container">
                <input
                  type="date"
                  value={formatDateForInput(selectedDate)}
                  onChange={handleDateChange}
                  className="date-picker"
                />
              </div>
            </div>
          </header>
          
          <div className="weekend-message">
            <div className="icon">🏢</div>
            <h2>Cantine fermée</h2>
            <p>La cantine est fermée le week-end.</p>
            <p className="selected-date">{formatDate(selectedDate)}</p>
            <p>Choisissez un jour de semaine pour voir le menu !</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedMenu) {
    return (
      <div className="app">
        <div className="container">
          <header className="header">
            <h1>🍽️ Cantine Scolaire</h1>
            <p className="subtitle">Mairie de Parmain</p>
            
            <div className="date-navigation">
              <div className="nav-buttons">
                <button onClick={() => navigateDate(-1)} className="nav-btn">
                  ← Jour précédent
                </button>
                {!isToday && (
                  <button onClick={goToToday} className="today-btn">
                    Aujourd'hui
                  </button>
                )}
                <button onClick={() => navigateDate(1)} className="nav-btn">
                  Jour suivant →
                </button>
              </div>
              
              <div className="date-picker-container">
                <input
                  type="date"
                  value={formatDateForInput(selectedDate)}
                  onChange={handleDateChange}
                  className="date-picker"
                />
              </div>
            </div>
          </header>
          
          <div className="no-data-message">
            <div className="icon">📅</div>
            <h2>Menu non disponible</h2>
            <p className="selected-date">{formatDate(selectedDate)}</p>
            <p>Le menu n'est pas encore disponible pour cette date.</p>
            <p>Essayez une autre date ou consultez le menu d'aujourd'hui.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <h1>🍽️ Menu {isToday ? 'du Jour' : 'de la Cantine'}</h1>
          <p className="subtitle">Mairie de Parmain</p>
          
          <div className="date-navigation">
            <div className="nav-buttons">
              <button onClick={() => navigateDate(-1)} className="nav-btn">
                ← Jour précédent
              </button>
              {!isToday && (
                <button onClick={goToToday} className="today-btn">
                  Aujourd'hui
                </button>
              )}
              <button onClick={() => navigateDate(1)} className="nav-btn">
                Jour suivant →
              </button>
            </div>
            
            <div className="date-picker-container">
              <input
                type="date"
                value={formatDateForInput(selectedDate)}
                onChange={handleDateChange}
                className="date-picker"
              />
            </div>
          </div>
          
          <p className="date">
            {formatDate(selectedDate)}
            {isToday && <span className="today-indicator"> • Aujourd'hui</span>}
          </p>
          
          {selectedMenu.vege && (
            <div className="vegetarian-badge">
              🌱 Repas Végétarien
            </div>
          )}
        </header>

        <div className="menu-card">
          <div className="menu-section">
            <div className="section-icon">🥗</div>
            <div className="menu-section-content">
              <h3>Entrée</h3>
              <p>{selectedMenu.starter}</p>
            </div>
          </div>

          <div className="menu-section">
            <div className="section-icon">🍖</div>
            <div className="menu-section-content">
              <h3>Plat principal</h3>
              <p>{selectedMenu.mainCourse}</p>
            </div>
          </div>

          <div className="menu-section">
            <div className="section-icon">🥕</div>
            <div className="menu-section-content">
              <h3>Accompagnement</h3>
              <p>{selectedMenu.side}</p>
            </div>
          </div>

          <div className="menu-section">
            <div className="section-icon">🧀</div>
            <div className="menu-section-content">
              <h3>Fromage / Laitage</h3>
              <p>{selectedMenu.dairy}</p>
            </div>
          </div>

          <div className="menu-section">
            <div className="section-icon">🍰</div>
            <div className="menu-section-content">
              <h3>Dessert</h3>
              <p>{selectedMenu.dessert}</p>
            </div>
          </div>
        </div>

        <footer className="footer">
          <p>Bon appétit ! 😋</p>
          <small>Menus susceptibles de variations, sous réserve des approvisionnements.</small>
        </footer>
      </div>
    </div>
  );
};

export default App;
